import { getGoSearchHost, isServerSide } from 'utils/host-config';
import {
  CompletionSuggestBaseResult,
  CompletionSuggestBaseRequest,
  CompletionSuggestBaseResponse,
  GetMilitaryBaseRequest,
  GetMilitaryBaseResponse,
  MilitaryBase,
  GeoPoint,
  PhraseSuggestBaseRequest,
  PhraseSuggestBaseResponse,
  PhraseSuggestBaseResult,
  Highlight,
  GeoProximity,
  SearchBaseRequest,
  SearchBaseResponse,
  SearchBaseResult,
  GeoBoundingBox,
} from '@zoocasa/go-search';
import { FetchOptions } from 'types/fetchWithRetry';
import { HttpRequestMethodTypes } from 'types/HttpRequestMethodTypes';
import { fetchWithRetry } from 'utils/fetchWithRetry';

const GET_MILITARY_BASE_ENDPOINT_URL = '/api/military/base';
const GET_MILITARY_BASE_SUGGESTIONS_ENDPOINT_URL = '/api/military/base/suggest';
const GET_MILITARY_BASE_NAME_SUGGESTIONS_ENDPOINT_URL =
  '/api/military/base/phrase-suggest';
const SEARCH_BASES_ENDPOINT_URL = '/api/military/base/search';

const DEFAULT_SUGGESTIONS_SIZE = 5;
const DEFAULT_CONFIDENCE = 1.0;
const DEFAULT_SEARCH_LIMIT = 5;

/**
 * A selector for a military base.
 *
 * NOTE: Only one of the properties should be provided.
 *
 * @property slug - The slug of the military base.
 * @property id - The id of the military base.
 * @property name - The name of the military base.
 */
export type GetBaseSelectorOneOf = {
  slug?: string;
  id?: string;
  name?: string;
};

/**
 * Fetches a military base by a given selector.
 *
 * If no selector is provided, the function will return undefined.
 *
 * If multiple selectors are provided, the function will use the
 * first non-undefined selector in the following order: slug, id, name.
 *
 * @param selector - The selector for the military base.
 * @param signal - The signal for the request.
 * @param host - The host for the request.
 * @returns The military base.
 */
export async function getBase(
  selector: GetBaseSelectorOneOf,
  signal?: AbortSignal,
  host: string = getGoSearchHost(isServerSide())
): Promise<MilitaryBase | undefined> {
  const getMilitaryBaseEndpointUrl = `${host}${GET_MILITARY_BASE_ENDPOINT_URL}`;
  if (!selector.slug && !selector.id && !selector.name) {
    return undefined;
  }

  try {
    let body: GetMilitaryBaseRequest;
    if (selector.slug) {
      body = { slug: selector.slug };
    } else if (selector.id) {
      body = { id: selector.id };
    } else if (selector.name) {
      body = { name: selector.name };
    }
    const request = GetMilitaryBaseRequest.fromJSON(body);
    const encodedBody = GetMilitaryBaseRequest.encode(request).finish();

    const options: FetchOptions = {
      method: HttpRequestMethodTypes.POST,
      body: encodedBody,
      signal,
    };
    const response = await fetchWithRetry(getMilitaryBaseEndpointUrl, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = GetMilitaryBaseResponse.decode(new Uint8Array(buffer));
      return apiResp.data;
    } else {
      console.error(
        `fetch military base: ${response.status} ${response.statusText} ${response.type}`
      );
      return undefined;
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch military base request was aborted');
    } else {
      console.error('failed to fetch military base: %s', error);
    }
    return undefined;
  }
}

export async function getBaseSuggestions(
  text: string,
  size: number = DEFAULT_SUGGESTIONS_SIZE,
  proximity?: GeoPoint,
  signal?: AbortSignal,
  host: string = getGoSearchHost(isServerSide())
): Promise<CompletionSuggestBaseResult[]> {
  const getMilitaryBaseEndpointUrl = `${host}${GET_MILITARY_BASE_SUGGESTIONS_ENDPOINT_URL}`;
  try {
    const requestBody: CompletionSuggestBaseRequest = {
      text,
      size,
      proximity,
    };
    const request = CompletionSuggestBaseRequest.fromPartial(requestBody);
    const encodedBody = CompletionSuggestBaseRequest.encode(request).finish();

    const options: FetchOptions = {
      method: HttpRequestMethodTypes.POST,
      body: encodedBody,
      signal,
    };
    const response = await fetchWithRetry(getMilitaryBaseEndpointUrl, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = CompletionSuggestBaseResponse.decode(
        new Uint8Array(buffer)
      );
      return apiResp.suggestions;
    } else {
      console.error(
        `fetch military base suggestions: ${response.status} ${response.statusText} ${response.type}`
      );
      return undefined;
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch military base suggestions request was aborted');
    } else {
      console.error('failed to fetch military base suggestions: %s', error);
    }
    return undefined;
  }
}

/**
 * Fetches a list of military base name suggestions.
 *
 * @param text The text to be used as the reference for the suggestions.
 * @param size The number of suggestions to return. The default is {@link DEFAULT_SUGGESTIONS_SIZE 5}.
 * @param highlight Sets up suggestion highlighting. If not provided
 * then no highlighted field is returned. If provided the pre and post tags
 * are wrapped around the changed tokens. If multiple tokens in a row are
 * changed the entire phrase of changed tokens is wrapped rather than each token.
 * @param confidence The confidence level defines a factor applied to the
 * input phrases score which is used as a threshold for other suggest candidates.
 * Only candidates that score higher than the threshold will be included in the result.
 * For instance a confidence level of 1.0 will only return suggestions
 * that score higher than the input phrase. If set to 0.0 the top N candidates
 * are returned. The default is 1.0.
 * @param signal The signal to use for the request. Optional.
 * @param host The host to use for the request. Optional.
 * @returns A list of military base name suggestions.
 */
export async function getBaseNameSuggestions(
  text: string,
  size: number = DEFAULT_SUGGESTIONS_SIZE,
  highlight?: Highlight,
  confidence: number = DEFAULT_CONFIDENCE,
  signal?: AbortSignal,
  host: string = getGoSearchHost(isServerSide())
): Promise<PhraseSuggestBaseResult | undefined> {
  const getMilitaryBaseEndpointUrl = `${host}${GET_MILITARY_BASE_NAME_SUGGESTIONS_ENDPOINT_URL}`;
  try {
    const requestBody: PhraseSuggestBaseRequest = {
      text,
      size,
      highlight,
      confidence,
    };
    const request = PhraseSuggestBaseRequest.fromPartial(requestBody);
    const encodedBody = PhraseSuggestBaseRequest.encode(request).finish();

    const options: FetchOptions = {
      method: HttpRequestMethodTypes.POST,
      body: encodedBody,
      signal,
    };
    const response = await fetchWithRetry(getMilitaryBaseEndpointUrl, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = PhraseSuggestBaseResponse.decode(new Uint8Array(buffer));
      return apiResp.suggestions;
    } else {
      console.error(
        `fetch military base name suggestions: ${response.status} ${response.statusText} ${response.type}`
      );
      return undefined;
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch military base suggestions request was aborted');
    } else {
      console.error('failed to fetch military base suggestions: %s', error);
    }
    return undefined;
  }
}

export async function getBasesByProximity(proximity: GeoProximity, limit: number = DEFAULT_SEARCH_LIMIT, signal?: AbortSignal, host: string = getGoSearchHost(isServerSide())): Promise<SearchBaseResult[]> {
  const searchMilitaryBaseEndpointUrl = `${host}${SEARCH_BASES_ENDPOINT_URL}`;
  try {
    const requestBody: SearchBaseRequest = {
      proximity,
      limit,
    };
    const request = SearchBaseRequest.fromPartial(requestBody);
    const encodedBody = SearchBaseRequest.encode(request).finish();

    const options: FetchOptions = {
      method: HttpRequestMethodTypes.POST,
      body: encodedBody,
      signal,
    };
    const response = await fetchWithRetry(searchMilitaryBaseEndpointUrl, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = SearchBaseResponse.decode(new Uint8Array(buffer));
      return apiResp.militaryBases;
    } else {
      console.error(
        `fetch military base suggestions: ${response.status} ${response.statusText} ${response.type}`
      );
      return [];
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch military base suggestions request was aborted');
    } else {
      console.error('failed to fetch military base suggestions: %s', error);
    }
  }
  return [];
}

export async function getBasesByBounds(boundingBox: GeoBoundingBox, limit: number = DEFAULT_SEARCH_LIMIT, signal?: AbortSignal, host: string = getGoSearchHost(isServerSide())): Promise<SearchBaseResult[]> {
  const searchMilitaryBaseEndpointUrl = `${host}${SEARCH_BASES_ENDPOINT_URL}`;
  try {
    const requestBody: SearchBaseRequest = {
      boundingBox,
      limit,
    };
    const request = SearchBaseRequest.fromPartial(requestBody);
    const encodedBody = SearchBaseRequest.encode(request).finish();

    const options: FetchOptions = {
      method: HttpRequestMethodTypes.POST,
      body: encodedBody,
      signal,
    };
    const response = await fetchWithRetry(searchMilitaryBaseEndpointUrl, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = SearchBaseResponse.decode(new Uint8Array(buffer));
      return apiResp.militaryBases;
    } else {
      console.error(
        `fetch military base suggestions: ${response.status} ${response.statusText} ${response.type}`
      );
      return [];
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch military base suggestions request was aborted');
    } else {
      console.error('failed to fetch military base suggestions: %s', error);
    }
  }
  return [];
}
